import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, useMediaQuery, useTheme, Box, Typography } from "@mui/material";
import SVGPlus from "@/assets/icons/SVGPlus.tsx";
import Modal from "@/components/Modal";
import MultiStepProjectForm from "@/pages/ProjectDetailsPage/components/MultiStepProjectForm.tsx";
import { useAuthStore } from "@/store/authStore.ts";
import { useUserStore } from "@/store/userStore.ts";
import { createProjectReq } from "@/api/projectsApi";
import { createContractorProjectReq } from "@/api/contractorApi";
import { getUserReq } from "@/api/adminApi";
import {
  PROJECT_ALREADY_EXISTS_ERROR_MESSAGE,
  SUPPORT_EMAIL,
} from "@/constants";
import { StyledRightActionButton } from "@/components/styled";
import { useErrorBoundary } from "@/libs/ErrorBoundary.tsx";
import Snackbar from "@mui/material/Snackbar";
import { FileRecord } from "@/types";
import {
  createProjectFormData,
  validateStepTransition,
} from "@/utils/projectFormUtils";
import Stepper from "@/components/Stepper";
import { PROJECT_STEPS, PROJECT_STEP_CONFIGS, ProjectStep } from "@/constants";

export type AddProjectForm = {
  customerName: string;
  accountHash: string;
  zipCode: string;
  phoneNumber?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  accountEmail: string;
  ratePlan?: string;
  careEligible?: boolean;
  feraEligible?: boolean;
  cocEligible?: boolean;

  solarSystemType: string[];
  solarKwdc?: number;
  solarKwac?: number;
  solarAnnualProduction?: number;
  newSolarKwdc?: number;
  newSolarKwac?: number;
  newSolarAnnualProduction?: number;
  externalIncentivePrograms?: string;
  externalIncentives?: string;
  thirdPartyOwned?: boolean;
  inverterManufacturer?: string;
  inverterName?: string;
  contractor?: string;
  contractorId?: string;
  batteryManufacturer?: string;
  batteryOemId?: string;
  batteryProductName?: string;
  batteryModelId?: string;
  usableCapacity?: string;
  usableCapacityIncentive?: string;
  batteryQuantity?: string;
  totalProjectCost?: string;

  upfrontIncentivePayment?: string;
  sbsi?: string;

  payeeType?: string;
  payeeName?: string;
  payeeEmail?: string;
  payeeAddress?: string;
  payeeCity?: string;
  payeeState?: string;
  payeeZip?: string;

  batteryOem?: string;
  batteryModel?: string;
  numberOfBatteries?: string;
  totalCapacity?: string;
  nominalPower?: string;
  totalNominalPower?: string;
  summerEnrolment?: string;
  winterEnrolment?: string;
  participation?: "weekdays" | "weekends" | "weekdays_weekends";
  participantEnrollment?: File | FileRecord;
  systemSizingTool?: File | FileRecord;
  contract?: File | FileRecord;
  operationalizedDate?: string;
  estimated_installation_date?: string | null;
  extendToDate?: string;
  extendToDateNote?: string;
  termsAndConditions?: boolean;
  termsAndConditionsFile?: File | FileRecord;
};

const AddProjectModal = ({
  getProjectsReq,
}: {
  getProjectsReq: () => void;
}) => {
  const [internalError, setInternalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<ProjectStep>(PROJECT_STEPS.PERSONAL_INFO);
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);

  const methods = useForm<AddProjectForm>({
    mode: "onChange",
    defaultValues: {
      batteryQuantity: "",
      contractor: "",
      thirdPartyOwned: false,
    },
  });

  const { handleSubmit, reset, setError, trigger } = methods;

  const handleClose = () => {
    setOpen(false);
    reset();
    setStep(1);
  };

  const handleCloseSnackbar = () => {
    setInternalError("");
  };

  const onSubmit = async (data: AddProjectForm) => {
    // SDCP-1286: guard against rapid double/triple submit creating duplicate
    // projects — ignore re-entrant calls while a create request is in flight.
    if (isSubmitting) {
      return;
    }

    const requiredFields = [
      "customerName",
      "accountHash",
      "zipCode",
      "streetAddress",
      "city",
      "state",
      "accountEmail",
      "contractor",
      "contractorId",
      "batteryManufacturer",
      "batteryProductName",
      "usableCapacity",
      "batteryQuantity",
      "upfrontIncentivePayment",
    ];

    const missingFields: string[] = [];
    requiredFields.forEach((field) => {
      const value = data[field as keyof AddProjectForm];
      if (
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "") ||
        (typeof value === "number" && isNaN(value))
      ) {
        missingFields.push(field);
      }
    });

    if (missingFields.length > 0) {
      setInternalError(`Missing required fields: ${missingFields.join(", ")}`);
      return;
    }

    if (
      !data.solarSystemType ||
      !Array.isArray(data.solarSystemType) ||
      data.solarSystemType.length === 0
    ) {
      setInternalError("Please select at least one solar system type");
      return;
    }

    const hasExistingSolar = data.solarSystemType.includes("existing");
    const hasNewSolar = data.solarSystemType.includes("new");

    const isSolarFieldsValid = (fields: {
      kwdc: number | undefined | null;
      kwac: number | undefined | null;
      annualProduction: number | undefined | null;
    }): boolean => {
      return Object.values(fields).every(
        (value) => value !== undefined && value !== null && value > 0
      );
    };

    if (hasExistingSolar) {
      if (
        !isSolarFieldsValid({
          kwdc: data.solarKwdc,
          kwac: data.solarKwac,
          annualProduction: data.solarAnnualProduction,
        })
      ) {
        setInternalError(
          "Please fill in all required fields for Existing Solar System"
        );
        return;
      }
    }

    if (hasNewSolar) {
      if (
        !isSolarFieldsValid({
          kwdc: data.newSolarKwdc,
          kwac: data.newSolarKwac,
          annualProduction: data.newSolarAnnualProduction,
        })
      ) {
        setInternalError(
          "Please fill in all required fields for New Solar System with positive values"
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const role = useAuthStore.getState().role;
      const formData = createProjectFormData(data, role || undefined);

      if (role === "contractor") {
        await createContractorProjectReq(formData);
      } else {
        await createProjectReq(formData);
      }

      getProjectsReq();
      handleClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error;

      const isDuplicateAccountError =
        errorMessage === PROJECT_ALREADY_EXISTS_ERROR_MESSAGE ||
        errorMessage?.toLowerCase().includes("account number already exists");

      if (isDuplicateAccountError) {
        setError("accountHash", {
          message: errorMessage,
          type: "manual",
        });
        setStep(PROJECT_STEPS.PERSONAL_INFO);
      } else {
        setInternalError(errorMessage || "Failed to create project");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep = async (newStep: ProjectStep) => {
    const accountHash = methods.getValues("accountHash");
    const zipCode = methods.getValues("zipCode");

    // If moving from step 2 to step 3, validate the email field
    if (
      step === PROJECT_STEPS.SYSTEM_INFO &&
      newStep === PROJECT_STEPS.INCENTIVE_CALC
    ) {
      const emailValid = await trigger("accountEmail");
      if (!emailValid) {
        return;
      }
    }

    const isValid = await validateStepTransition(
      newStep,
      setError,
      accountHash,
      zipCode
    );
    if (isValid) {
      setStep(newStep);
    }
  };

  return (
    <>
      <StyledRightActionButton
        sx={{
          gap: "6px",
          whiteSpace: "nowrap",
          ...(!downSm && { minWidth: "180px" }),
        }}
        onClick={() => setOpen(true)}
        variant={"contained"}
      >
        <SVGPlus />
        {!downSm && "Add Project"}
      </StyledRightActionButton>
      <Modal
        open={open}
        onClose={handleClose}
        title={"Add Project"}
        subtitle={""}
        boxSx={{ maxWidth: "700px" }}
      >
        <>
          <Box sx={{ mt: 3, mb: 4, position: "relative", px: 0 }}>
            <Stepper steps={PROJECT_STEP_CONFIGS} currentStep={step} />
          </Box>
          <FormProvider {...methods}>
            <MultiStepProjectForm
              handleSubmit={handleSubmit(onSubmit)}
              handleClose={handleClose}
              handleStep={handleStep}
              step={step}
              isSubmitting={isSubmitting}
            />
          </FormProvider>
          {!!internalError && (
            <Snackbar
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              sx={{ zIndex: 1000 }}
              open={!!internalError}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
            >
              <Alert onClose={handleCloseSnackbar} severity="error">
                {internalError}
              </Alert>
            </Snackbar>
          )}
        </>
      </Modal>
    </>
  );
};

export default AddProjectModal;
